import React from 'react'
import { Box, Drawer, IconButton, Typography, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Avatar, Button, ColorDot, SearchInput, useDesignSystem } from '@/design-system'
import { useAuth, useLocalStorage } from '@/hooks/common'
import { useGetBasicInfo } from '@/hooks/api/dashboard'
import { useAlbumLibrary } from '../hooks/useAlbumLibrary'
import { EgowallLogo, LogoMark } from '../components/EgowallLogo'
import MenuIcon from '@mui/icons-material/Menu'
import toast from 'react-hot-toast'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'

const MOBILE_BREAKPOINT = '(max-width:899px)'

const Shell = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  color: 'var(--ds-ink)',
  fontFamily: 'var(--ds-font)',
  background: 'var(--ds-surface)',
})

const Sidebar = styled(Box)({
  width: 232,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '18px 12px',
  gap: 2,
  position: 'sticky',
  top: 0,
  height: '100vh',
  background: 'var(--ds-canvas)',
  borderRight: '1px solid var(--ds-border)',
})

const LogoRow = styled(Box)({
  padding: '6px 10px 16px',
})

const NavItem = styled('button', {
  shouldForwardProp: (p) => p !== '$active' && p !== '$disabled',
})<{ $active?: boolean; $disabled?: boolean }>(({ $active, $disabled }) => ({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  height: 36,
  padding: '0 10px',
  borderRadius: 9,
  fontSize: 13.5,
  fontWeight: $active ? 640 : 550,
  cursor: 'pointer',
  width: '100%',
  boxSizing: 'border-box',
  background: $active ? 'var(--ds-hover)' : 'transparent',
  color: $active ? 'var(--ds-ink)' : 'var(--ds-ink-muted)',
  opacity: $disabled ? 0.55 : 1,
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': $disabled
    ? undefined
    : {
        background: 'var(--ds-hover)',
        color: 'var(--ds-ink)',
      },
  '& .nav-ic': {
    width: 17,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.75,
    flexShrink: 0,
    fontSize: 14.5,
    '& svg': { fontSize: 17 },
  },
  '& .nav-count': {
    marginLeft: 'auto',
    fontFamily: 'var(--ds-font-mono)',
    fontSize: 10,
    color: 'var(--ds-ink-subtle)',
  },
  '& .nav-badge': {
    marginLeft: 'auto',
    fontFamily: 'var(--ds-font-mono)',
    fontSize: 10,
    color: '#fff',
    background: 'var(--ds-accent-deep-a, #e97a4a)',
    borderRadius: 8,
    padding: '1px 6px',
  },
}))

const SideLabel = styled(Typography)({
  padding: '16px 10px 6px',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
})

const DotRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexWrap: 'wrap',
  padding: '14px 10px 4px',
})

const SidebarFoot = styled(Box)({
  marginTop: 'auto',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '10px',
  borderRadius: 9,
  cursor: 'pointer',
  transition: 'background 120ms ease',
  '&:hover': { background: 'var(--ds-bg-dip, rgba(0,0,0,0.04))' },
  '&[data-active="true"]': { background: 'var(--ds-bg-dip, rgba(0,0,0,0.06))' },
})

// Opens upward — the trigger sits at the very bottom of the sidebar, so a
// menu dropping down would render mostly off-screen.
const AccountMenu = styled(Box, { shouldForwardProp: (p) => p !== '$open' })<{ $open: boolean }>(({ $open }) => ({
  position: 'absolute',
  bottom: 'calc(100% + 6px)',
  left: 4,
  right: 4,
  background: 'var(--ds-canvas)',
  borderRadius: 10,
  boxShadow: 'var(--ds-shadow-deep)',
  padding: 5,
  zIndex: 30,
  opacity: $open ? 1 : 0,
  visibility: $open ? 'visible' : 'hidden',
  transform: $open ? 'translateY(0)' : 'translateY(4px)',
  transition: 'all 140ms ease',
}))

const AccountMenuItem = styled('button', {
  shouldForwardProp: (p) => p !== '$danger',
})<{ $danger?: boolean }>(({ $danger }) => ({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  boxSizing: 'border-box',
  textAlign: 'left',
  padding: '8px 10px',
  borderRadius: 7,
  fontSize: 12.5,
  fontWeight: 550,
  cursor: 'pointer',
  color: $danger ? 'var(--ds-accent-deep-a, #e97a4a)' : 'var(--ds-ink-muted)',
  '&:hover': $danger
    ? { background: 'color-mix(in srgb, var(--ds-accent-deep-a, #e97a4a) 10%, transparent)' }
    : { background: 'var(--ds-hover)', color: 'var(--ds-ink)' },
}))

const AccountMenuDivider = styled(Box)({
  height: 1,
  margin: '4px 6px',
  background: 'var(--ds-border)',
})

const Divider = styled(Box)({
  width: 1,
  flexShrink: 0,
  alignSelf: 'stretch',
  backgroundColor: 'var(--ds-border)',
})

const Main = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
})

const TopBar = styled(Box)({
  height: 60,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '0 28px',
  borderBottom: '1px solid var(--ds-border)',
  background: 'var(--ds-canvas)',
  position: 'sticky',
  top: 0,
  zIndex: 40,
})

const SearchWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: 300,
  maxWidth: '100%',
  height: 34,
  padding: '0 12px',
  borderRadius: 9,
  background: 'var(--ds-hover)',
  '& > label': { flex: 1, minWidth: 0 },
  '& input': { width: '100%' },
})

const Content = styled(Box)({
  padding: '30px 28px 60px',
  flex: 1,
})

const MobileTopBar = styled(Box)({
  height: 56,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 14px',
  borderBottom: '1px solid var(--ds-border)',
  background: 'var(--ds-canvas)',
})

type NavDef = {
  id: string
  label: string
  icon: string
  path?: string
  active?: boolean
  count?: number
  /** Coral pill instead of the plain mono count — used for unread-style signals */
  badge?: number
  disabled?: boolean
}

export function AlbumShellLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const { theme } = useDesignSystem()
  const { user, logout } = useAuth()
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false)
  const accountMenuRef = React.useRef<HTMLDivElement>(null)
  const [basicInfo] = useLocalStorage('ewBasicInfo', null)
  const { albums, loading, error, refresh } = useAlbumLibrary()
  // The album product layer never had its own version of the legacy
  // Header/ResponsiveNavBar mount effect that warms ewBasicInfo (mobileContID,
  // etc.) — nothing under /albums/* fetched it unless you'd also visited
  // /settings or a profile page first. Warm it once here so containerID is
  // ready before any upload attempt, no matter where the session enters.
  const getBasicInfo = useGetBasicInfo(user?.userID ?? '')
  React.useEffect(() => {
    if (!user?.userID) return
    getBasicInfo.mutate({ isUserProfile: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userID])
  const [unreadCount] = useAppStore((state) => [state.notification.unreadCount], shallow)
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()
  const [filterDot, setFilterDot] = React.useState<string | null>(null)
  const [query, setQuery] = React.useState('')
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false)
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const onAlbums = pathname.startsWith('/albums')
  const onGalleries = pathname.startsWith('/galleries')
  const onSpaces = pathname.startsWith('/spaces')
  const onSettings = pathname.startsWith('/settings')
  const onOwnProfile = pathname === '/profile/me' || pathname.startsWith('/profile/me/')
  const onConnections = pathname.startsWith('/connections')
  const onDiscover = pathname.startsWith('/discover')
  const onSearch = pathname.startsWith('/search')
  const walkableCount = albums.filter((a) => a.galleryId).length
  const connCount = Number(basicInfo?.connCount) || 0

  // Keep the shell's search box reflecting what's actually been searched when
  // landing on /search (deep link, back/forward nav, or a fresh Enter here).
  React.useEffect(() => {
    if (onSearch) setQuery(searchParams.get('key') || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSearch, searchParams])

  // Global ⌘K/Ctrl+K — the shell's search box has always shown this hint, but
  // nothing backed it. Focuses (and selects, for a quick overtype) the same
  // input Enter already sends to /search from.
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
        searchInputRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  React.useEffect(() => {
    if (!accountMenuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) setAccountMenuOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountMenuOpen(false)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [accountMenuOpen])

  const primaryNav: NavDef[] = [
    // Leads the sidebar — Discover is other members' content, everything
    // below it is "my stuff." Logo click mirrors this (see navContent).
    { id: 'discover', label: 'Discover', icon: '✦', path: '/discover', active: onDiscover },
    { id: 'albums', label: 'Albums', icon: '▤', path: '/albums', active: onAlbums, count: albums.length },
    { id: 'galleries', label: '3D Galleries', icon: '◈', path: '/galleries', active: onGalleries, count: walkableCount },
    // Spaces nav link removed for now — route/screens still exist (see
    // spaces/routes.tsx), just not linked from the sidebar yet.
    { id: 'keys', label: 'Keys', icon: '⚿', disabled: true },
    { id: 'shared', label: 'Shared with me', icon: '👥', disabled: true },
  ]

  const onNotifications = pathname.startsWith('/notification')

  // Connections lives here now, not in the primary "my content" group —
  // connection requests are activity you react to, same shape as
  // notifications, not a library you browse.
  const activityNav: NavDef[] = [
    {
      id: 'notifications',
      label: 'Notifications',
      icon: '🔔',
      path: '/notification',
      active: onNotifications,
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
    { id: 'connections', label: 'Connections', icon: '◎', path: '/connections', active: onConnections, count: connCount },
  ]

  const footerNav: NavDef[] = [
    { id: 'settings', label: 'Settings', icon: '⚙', path: '/settings', active: onSettings },
  ]

  const goTo = (item: NavDef) => {
    if (item.disabled || !item.path) {
      toast('Coming soon')
      return
    }
    navigate(item.path)
    setMobileNavOpen(false)
  }

  const renderNavItem = (item: NavDef) => (
    <NavItem key={item.id} $active={item.active} $disabled={item.disabled} onClick={() => goTo(item)}>
      <span className='nav-ic'>{item.icon}</span>
      {item.label}
      {item.badge != null && <span className='nav-badge'>{item.badge}</span>}
      {item.count != null && item.count > 0 && <span className='nav-count'>{item.count}</span>}
    </NavItem>
  )

  const userInitial = user?.userName?.[0]?.toUpperCase() ?? '?'
  const shortName = basicInfo?.firstName
    ? `${basicInfo.firstName}${basicInfo.lastName ? ` ${basicInfo.lastName[0].toUpperCase()}.` : ''}`
    : user?.userName || 'Account'

  const navContent = (
    <>
      <LogoRow>
        <EgowallLogo onClick={() => navigate('/discover')} />
      </LogoRow>

      <Box display='flex' flexDirection='column' gap='2px'>
        {primaryNav.map(renderNavItem)}
      </Box>

      {onAlbums && (
        <DotRow>
          {theme.accentDots.map((c) => (
            <ColorDot
              key={c}
              color={c}
              size={14}
              selected={filterDot === c}
              label={`Filter by ${c}`}
              onClick={() => setFilterDot((prev) => (prev === c ? null : c))}
            />
          ))}
        </DotRow>
      )}

      <SideLabel>Activity</SideLabel>
      <Box display='flex' flexDirection='column' gap='2px'>
        {activityNav.map(renderNavItem)}
      </Box>

      <Box sx={{ flex: 1 }} />

      <Box display='flex' flexDirection='column' gap='2px'>
        {footerNav.map(renderNavItem)}
      </Box>

      <SidebarFoot
        ref={accountMenuRef}
        data-active={onOwnProfile || accountMenuOpen ? 'true' : undefined}
        role='button'
        tabIndex={0}
        aria-haspopup='menu'
        aria-expanded={accountMenuOpen}
        aria-label='Account menu'
        onClick={() => setAccountMenuOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setAccountMenuOpen((o) => !o)
          }
        }}
      >
        <Avatar
          sx={{
            width: 22,
            height: 22,
            fontSize: 9,
            fontWeight: 700,
            background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
            color: '#fff',
          }}
        >
          {userInitial}
        </Avatar>
        <Typography sx={{ fontSize: 13.5, fontWeight: 550, color: 'var(--ds-ink-muted)' }} noWrap>
          {shortName}
        </Typography>

        <AccountMenu $open={accountMenuOpen} role='menu' onClick={(e) => e.stopPropagation()}>
          <AccountMenuItem
            role='menuitem'
            onClick={() => {
              setAccountMenuOpen(false)
              navigate('/profile/me')
              setMobileNavOpen(false)
            }}
          >
            <span aria-hidden>◎</span> View profile
          </AccountMenuItem>
          <AccountMenuItem
            role='menuitem'
            onClick={() => {
              setAccountMenuOpen(false)
              navigate('/settings')
              setMobileNavOpen(false)
            }}
          >
            <span aria-hidden>⚙</span> Settings
          </AccountMenuItem>
          <AccountMenuDivider aria-hidden />
          <AccountMenuItem role='menuitem' $danger onClick={logout}>
            <span aria-hidden>⏻</span> Sign out
          </AccountMenuItem>
        </AccountMenu>
      </SidebarFoot>
    </>
  )

  return (
    <Shell className='ds-canvas'>
      {isMobile ? (
        <Drawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}>
          <Box sx={{ width: 232, height: '100%', display: 'flex', flexDirection: 'column', p: '18px 12px' }}>
            {navContent}
          </Box>
        </Drawer>
      ) : (
        <>
          <Sidebar>{navContent}</Sidebar>
          <Divider aria-hidden />
        </>
      )}

      <Main>
        {isMobile && (
          <MobileTopBar>
            <IconButton size='small' onClick={() => setMobileNavOpen(true)} aria-label='Open navigation'>
              <MenuIcon />
            </IconButton>
            <EgowallLogo onClick={() => navigate('/discover')} />
          </MobileTopBar>
        )}

        <TopBar sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', height: isMobile ? 'auto' : 60, py: isMobile ? 1.5 : 0 }}>
          <SearchWrap sx={{ width: isMobile ? '100%' : 300 }}>
            <SearchInput
              ref={searchInputRef}
              icon='⌕'
              hint={isMobile ? undefined : '⌘K'}
              placeholder={
                onSpaces
                  ? 'Search spaces, rooms, templates…'
                  : onGalleries
                    ? 'Search galleries, people, moments…'
                    : 'Search albums, people, moments…'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                const trimmed = query.trim()
                if (!trimmed) return
                navigate(`/search?key=${encodeURIComponent(trimmed)}`)
              }}
              sx={{ fontSize: 13 }}
            />
          </SearchWrap>
          <Box sx={{ flex: 1 }} />
          <Button
            variant='ghost'
            size='sm'
            onClick={() => {
              if (!isFeatureAvailable(FEATURE_KEYS.GALLERY_3D)) {
                showUpgrade({ featureKey: FEATURE_KEYS.GALLERY_3D })
                return
              }
              navigate('/galleries/try-3d')
            }}
          >
            ◈ Try 3D
          </Button>
          <Button variant='primary' size='sm' onClick={() => navigate('/albums/new')}>
            ＋ New album
          </Button>
        </TopBar>

        <Content>
          <Outlet context={{ filterDot, query, albums, albumsLoading: loading, albumsError: error, refreshAlbums: refresh }} />
        </Content>
      </Main>
    </Shell>
  )
}
